/**
 * Safe date parsing utilities.
 *
 * Wraps `new Date()` so callers never encounter an Invalid Date throw.
 */

import { APP_TIMEZONE } from '@/config/environment';

/**
 * Safe wrapper around `Date.toISOString()`.
 * Returns epoch-zero string if the conversion throws (e.g. Invalid Date).
 */
export function toSafeISOString(date: Date): string {
  try {
    return date.toISOString();
  } catch {
    return new Date(0).toISOString();
  }
}

/**
 * Parse a date value into a `YYYY-MM-DD` string suitable for `<input type="date">`.
 *
 * Handles:
 *  - `null` / `undefined` / empty string  -> today (or `fallback` if provided)
 *  - ISO strings (`2024-03-15T00:00:00Z`) -> splits on `T`
 *  - Already `YYYY-MM-DD`                 -> pass-through
 *  - Malformed values                     -> `fallback` (defaults to today)
 */
export function parseDateToLocalString(
  value: string | number | Date | null | undefined,
  fallback?: string
): string {
  const todayStr = () =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: APP_TIMEZONE,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());

  const fb = fallback ?? todayStr();

  if (value == null || value === '') return fb;

  // If it's already a YYYY-MM-DD string, return as-is
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // If it's an ISO string, split on T
  if (typeof value === 'string' && value.includes('T')) {
    const datePart = value.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      return datePart;
    }
  }

  // Try parsing as a Date — use UTC accessors to avoid timezone-induced date shifts
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return fb;
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  } catch {
    return fb;
  }
}

/**
 * Format a date value as DD/MM/YYYY.
 *
 * Uses UTC accessors so the date is not shifted by the client's timezone.
 */
export function formatDate(
  value: string | number | Date | null | undefined
): string {
  if (value == null || value === '') return '';
  const date = new Date(value as string);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Format a timestamp string for display in IST (Asia/Kolkata).
 * Uses Intl.DateTimeFormat so clients with a different local TZ
 * still see the canonical IST value.
 *
 * Date portion is always DD/MM/YYYY.
 *
 * @param iso  ISO 8601 string or Date-parseable value
 * @param style 'date' | 'time' | 'datetime' (default: 'datetime')
 */
export function formatTimestamp(
  iso: string | number | Date | null | undefined,
  style: 'date' | 'time' | 'datetime' = 'datetime'
): string {
  if (iso == null || iso === '') return '';

  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';

  if (style === 'date') {
    return formatDate(date);
  }

  const options: Intl.DateTimeFormatOptions = { timeZone: APP_TIMEZONE };
  options.hour = '2-digit';
  options.minute = '2-digit';

  const timePart = new Intl.DateTimeFormat('en-IN', options).format(date);

  if (style === 'time') return timePart;

  // datetime: convert to IST date then format as DD/MM/YYYY
  const istDate = new Date(
    date.toLocaleString('en-US', { timeZone: APP_TIMEZONE })
  );
  const day = String(istDate.getDate()).padStart(2, '0');
  const month = String(istDate.getMonth() + 1).padStart(2, '0');
  const year = istDate.getFullYear();

  return `${day}/${month}/${year}, ${timePart}`;
}
